import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useQuery } from "@tanstack/react-query";
import Svg, { Defs, LinearGradient, Rect, Stop } from "react-native-svg";
import { ChevronLeft, Heart, Music2, Play } from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import {
  getItems,
  getMusicGenres,
  imageSource,
  JellyfinItem,
} from "../api/jellyfin";
import { musicPlaylistsOnly } from "../utils/musicPlaylists";
import { useSession } from "../session/SessionProvider";
import { FocusablePressable } from "../components/FocusablePressable";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { MusicLibrarySidebar } from "./MusicHomeScreen";
import { colors, systemSansFont } from "../theme/tokens";
import { useMusicPlayback } from "../music/MusicPlaybackProvider";
import { useMusicScrollIndicator } from "../components/MusicScrollIndicator";
import { MusicEqualizer } from "../components/MusicEqualizer";

type Props = NativeStackScreenProps<RootStackParamList, "Music">;
type DiscographyTab = "all" | "albums" | "singles";

const GENRE_COLORS = [
  "#e13300",
  "#1e3264",
  "#477d95",
  "#8d67ab",
  "#537aa1",
  "#af2896",
  "#1e8a4e",
  "#e8115b",
  "#b02897",
  "#148a08",
  "#dc148c",
  "#056952",
  "#1e5799",
  "#e3b52a",
  "#c87d3e",
  "#e50914",
  "#0d73ec",
  "#7d3f98",
  "#2d46b9",
  "#1ba954",
];

function genreColor(name: string) {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1)
    hash = name.charCodeAt(index) + ((hash << 5) - hash);
  return GENRE_COLORS[Math.abs(hash) % GENRE_COLORS.length];
}

function musicTime(ticks?: number) {
  if (!ticks) return "—";
  const seconds = Math.round(ticks / 10000000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

export function MusicLibraryScreen({ navigation, route }: Props) {
  const { width } = useReferenceWindowDimensions();
  const compact = width <= 700;
  const phone = width < 600;
  const [sidebarOverride, setSidebarOverride] = useState<boolean>();
  const sidebarCollapsed = sidebarOverride ?? width <= 950;
  const { credentials } = useSession();
  const musicPlayback = useMusicPlayback();
  const musicScrollbar = useMusicScrollIndicator();
  const bottomNavInset = useBottomNavInset();
  const view = route.params?.view ?? "browse";
  const genreId = route.params?.genreId;
  const artistId = route.params?.artistId;
  const [discographyTab, setDiscographyTab] = useState<DiscographyTab>("all");
  const enabled = Boolean(credentials);
  const albums = useQuery({
    queryKey: ["music-library-shell", "albums"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        SortBy: "DateCreated",
        SortOrder: "Descending",
        Limit: 40,
        Fields: "AlbumArtist,ArtistItems,UserData",
      }),
    enabled,
    staleTime: 60_000,
  });
  const playlists = useQuery({
    queryKey: ["music-library-shell", "playlists"],
    queryFn: async () =>
      musicPlaylistsOnly(await getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "Playlist",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Fields: "MediaType,UserData",
      })),
    enabled,
    staleTime: 60_000,
  });
  const sidebarArtists = useQuery({
    queryKey: ["music-library-shell", "artists"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicArtist",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Limit: 40,
        Fields: "UserData",
      }),
    enabled,
    staleTime: 60_000,
  });
  const genres = useQuery({
    queryKey: ["music-browse-genres"],
    queryFn: () => getMusicGenres(credentials!, 150),
    enabled: enabled && view === "browse",
    staleTime: 60_000,
  });
  const liked = useQuery({
    queryKey: ["music-liked-songs"],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "Audio",
        Filters: "IsFavorite",
        SortBy: "SortName",
        SortOrder: "Ascending",
        Limit: 500,
        Fields:
          "Album,AlbumId,AlbumArtist,AlbumPrimaryImageTag,ArtistItems,UserData",
      }),
    enabled: enabled && view === "liked",
    staleTime: 30_000,
  });
  const discography = useQuery({
    queryKey: ["music-discography", artistId],
    queryFn: () =>
      getItems(credentials!, {
        Recursive: true,
        IncludeItemTypes: "MusicAlbum",
        ArtistIds: artistId ?? "",
        SortBy: "ProductionYear,SortName",
        SortOrder: "Descending,Ascending",
        Limit: 300,
        Fields: "AlbumArtist,ArtistItems,ChildCount,UserData",
      }),
    enabled: enabled && view === "discography" && Boolean(artistId),
    staleTime: 60_000,
  });
  const columns = compact ? 2 : width >= 1500 ? 6 : width >= 1150 ? 5 : 4;
  const sidebarWidth = phone ? 0 : sidebarCollapsed ? 72 : 320;
  const panelWidth = width - (phone ? 8 : sidebarWidth + 24);
  const pagePadding = compact ? 16 : 32;
  const gap = compact ? 12 : 16;
  const cardSize = Math.max(
    120,
    Math.floor((panelWidth - pagePadding * 2 - gap * (columns - 1)) / columns),
  );
  const sidebar = !phone ? (
    <MusicLibrarySidebar
      playlists={playlists.data ?? []}
      albums={albums.data ?? []}
      artists={sidebarArtists.data ?? []}
      collapsed={sidebarCollapsed}
      onToggleCollapsed={() => setSidebarOverride((value) => !(value ?? width <= 950))}
      activeDestination={
        view === "liked" ? "liked" : !genreId ? "browse" : undefined
      }
      onOpen={(entry) =>
        entry.Id && navigation.navigate("Item", { itemId: entry.Id })
      }
      onDestination={(destination) =>
        navigation.replace("Music", { view: destination })
      }
    />
  ) : null;
  if (!credentials) return null;
  const genreData = genres.data ?? [];
  const genreLoading = genres.isLoading;
  const discographyData = (discography.data ?? []).filter((album) =>
    discographyTab === "all"
      ? true
      : discographyTab === "singles"
        ? album.ChildCount != null && album.ChildCount < 4
        : album.ChildCount == null || album.ChildCount >= 4,
  );
  return (
    <View style={styles.safe}>
      <View style={[styles.shell, compact && styles.shellCompact]}>
        {sidebar}
        <View style={styles.mainPanel}>
        {view === "liked" ? (
          <FlatList
            {...musicScrollbar.listProps}
            style={styles.mainList}
            data={liked.data ?? []}
            keyExtractor={(item, index) => item.Id ?? `liked-${index}`}
            contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
            ListHeaderComponent={
              <>
                <View
                  style={[
                    styles.likedHeader,
                    compact && styles.likedHeaderCompact,
                  ]}
                >
                  <Svg
                    pointerEvents="none"
                    width={panelWidth}
                    height={compact ? 330 : 280}
                    style={styles.absolute}
                  >
                    <Defs>
                      <LinearGradient
                        id="liked-gradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <Stop offset="0" stopColor="#5038a0" />
                        <Stop offset="0.6" stopColor="#3d2d80" />
                        <Stop offset="1" stopColor="#121212" />
                      </LinearGradient>
                    </Defs>
                    <Rect
                      width={panelWidth}
                      height={compact ? 330 : 280}
                      fill="url(#liked-gradient)"
                    />
                  </Svg>
                  <View
                    style={[styles.likedArt, compact && styles.likedArtCompact]}
                  >
                    <Svg pointerEvents="none" width={compact ? 120 : 200} height={compact ? 120 : 200} style={styles.absolute}>
                      <Defs>
                        <LinearGradient id="liked-art-gradient" x1="0" y1="0" x2="1" y2="1">
                          <Stop offset="0" stopColor="#450af5" />
                          <Stop offset="1" stopColor="#c4efd9" />
                        </LinearGradient>
                      </Defs>
                      <Rect width={compact ? 120 : 200} height={compact ? 120 : 200} fill="url(#liked-art-gradient)" />
                    </Svg>
                    <Heart color="#fff" fill="#fff" size={compact ? 40 : 64} style={styles.likedHeart} />
                  </View>
                  <View style={styles.likedCopy}>
                    <Text style={styles.type}>Playlist</Text>
                    <Text
                      style={[
                        styles.likedTitle,
                        compact && styles.likedTitleCompact,
                      ]}
                    >
                      Liked Songs
                    </Text>
                    <Text style={styles.summary}>
                      {liked.data ? `${liked.data.length} songs` : "…"}
                    </Text>
                  </View>
                </View>
                <Svg pointerEvents="none" width={panelWidth} height={360} style={[styles.absolute, { top: compact ? 330 : 280 }]}>
                  <Defs>
                    <LinearGradient id="liked-body-gradient" x1="0" y1="0" x2="0" y2="1">
                      <Stop offset="0" stopColor="#1e1e2e" />
                      <Stop offset="1" stopColor="#121212" />
                    </LinearGradient>
                  </Defs>
                  <Rect width={panelWidth} height={360} fill="url(#liked-body-gradient)" />
                </Svg>
                <View
                  style={[styles.controls, compact && styles.controlsCompact]}
                >
                  <FocusablePressable
                    accessibilityLabel="Play liked songs"
                    onPress={() =>
                      musicPlayback.loadQueue(liked.data ?? [], 0, true)
                    }
                    style={(focused, pressed, hovered) => [
                      styles.play,
                      (focused || hovered) && styles.playActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <Play color="#fff" fill="#fff" size={23} />
                  </FocusablePressable>
                </View>
                {!compact ? (
                  <View style={styles.trackHeader}>
                    <Text style={styles.indexHeader}>#</Text>
                    <Text style={styles.trackHeading}>Title</Text>
                    <Text style={styles.albumHeading}>Album</Text>
                    <Text style={styles.timeHeading}>Duration</Text>
                  </View>
                ) : null}
              </>
            }
            renderItem={({ item, index }) => (
              <FocusablePressable
                accessibilityLabel={`Play ${item.Name ?? `track ${index + 1}`}`}
                onPress={() =>
                  musicPlayback.loadQueue(liked.data ?? [], index, true)
                }
                style={(focused, pressed, hovered) => [
                  styles.track,
                  compact && styles.trackCompact,
                  (focused || hovered) && styles.trackActive,
                  pressed && styles.pressed,
                ]}
              >
                <View style={styles.index}>
                  {musicPlayback.currentTrack?.Id === item.Id && musicPlayback.playing
                    ? <MusicEqualizer />
                    : <Text style={styles.indexText}>{index + 1}</Text>}
                </View>
                <Image
                  source={imageSource(credentials, item, "Primary", 80)}
                  resizeMode="cover"
                  style={styles.trackArt}
                />
                <View style={styles.trackCopy}>
                  <Text numberOfLines={1} style={[styles.trackTitle, musicPlayback.currentTrack?.Id === item.Id && styles.trackTitleCurrent]}>
                    {item.Name ?? "Untitled"}
                  </Text>
                  <Text numberOfLines={1} style={styles.trackArtist}>
                    {item.ArtistItems?.map((artist) => artist.Name)
                      .filter(Boolean)
                      .join(", ") ||
                      item.AlbumArtist ||
                      "Unknown artist"}
                  </Text>
                </View>
                {!compact ? (
                  <Text numberOfLines={1} style={styles.album}>
                    {item.Album ?? "Unknown album"}
                  </Text>
                ) : null}
                <Text style={styles.time}>{musicTime(item.RunTimeTicks)}</Text>
              </FocusablePressable>
            )}
            ListEmptyComponent={
              liked.isLoading ? (
                <ActivityIndicator color={colors.brand} style={styles.loader} />
              ) : (
                <Text style={styles.empty}>
                  No liked songs yet. Heart a track to add it here.
                </Text>
              )
            }
          />
        ) : view === "discography" ? (
          <FlatList
            {...musicScrollbar.listProps}
            style={styles.mainList}
            data={discographyData}
            numColumns={columns}
            key={`discography-${columns}`}
            keyExtractor={(item, index) => item.Id ?? `discography-${index}`}
            contentContainerStyle={[
              styles.browseContent,
              { paddingHorizontal: pagePadding },
              bottomNavInset ? { paddingBottom: bottomNavInset } : undefined,
            ]}
            columnWrapperStyle={styles.genreRow}
            ListHeaderComponent={
              <View>
                <FocusablePressable
                  accessibilityLabel="Back to artist"
                  onPress={() =>
                    artistId && navigation.replace("Item", { itemId: artistId })
                  }
                  style={(focused, pressed, hovered) => [
                    styles.discographyBack,
                    (focused || hovered) && styles.discographyBackActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <ChevronLeft color="#b3b3b3" size={18} />
                  <Text style={styles.discographyBackText}>Back to artist</Text>
                </FocusablePressable>
                <Text style={styles.discographyTitle}>Discography</Text>
                <View style={styles.discographyTabs}>
                  {(["all", "albums", "singles"] as DiscographyTab[]).map(
                    (tab) => (
                      <FocusablePressable
                        key={tab}
                        accessibilityLabel={`Show ${tab}`}
                        onPress={() => setDiscographyTab(tab)}
                        style={(focused, pressed, hovered) => [
                          styles.discographyTab,
                          discographyTab === tab && styles.discographyTabActive,
                          (focused || hovered) && styles.discographyTabHovered,
                          pressed && styles.pressed,
                        ]}
                      >
                        <Text
                          style={[
                            styles.discographyTabText,
                            discographyTab === tab &&
                              styles.discographyTabTextActive,
                          ]}
                        >
                          {tab.slice(0, 1).toUpperCase() + tab.slice(1)}
                        </Text>
                      </FocusablePressable>
                    ),
                  )}
                </View>
              </View>
            }
            renderItem={({ item }) => (
              <FocusablePressable
                accessibilityLabel={`Open ${item.Name ?? "album"}`}
                onPress={() =>
                  item.Id && navigation.navigate("Item", { itemId: item.Id })
                }
                style={(focused, pressed, hovered) => [
                  styles.discographyCard,
                  { width: cardSize },
                  (focused || hovered) && styles.genreCardActive,
                  pressed && styles.pressed,
                ]}
              >
                <View
                  style={[
                    styles.discographyArt,
                    { width: cardSize, height: cardSize },
                  ]}
                >
                  {item.ImageTags?.Primary ? (
                    <Image
                      source={imageSource(
                        credentials,
                        item,
                        "Primary",
                        cardSize * 2,
                      )}
                      resizeMode="cover"
                      style={{ width: cardSize, height: cardSize }}
                    />
                  ) : (
                    <Music2 color="#777" size={Math.round(cardSize * 0.3)} />
                  )}
                </View>
                <Text numberOfLines={1} style={styles.discographyCardTitle}>
                  {item.Name ?? "Untitled"}
                </Text>
                <Text numberOfLines={1} style={styles.discographyCardSubtitle}>
                  {[item.ProductionYear, item.AlbumArtist].filter(Boolean).join(" · ") || "Album"}
                </Text>
              </FocusablePressable>
            )}
            ListEmptyComponent={
              discography.isLoading ? (
                <ActivityIndicator color={colors.brand} style={styles.loader} />
              ) : (
                <Text style={styles.empty}>No {discographyTab} found.</Text>
              )
            }
          />
        ) : (
          <FlatList
            {...musicScrollbar.listProps}
            style={styles.mainList}
            data={genreData}
            numColumns={columns}
            key={`genres-${columns}`}
            keyExtractor={(item, index) => item.Id ?? `genre-${index}`}
            contentContainerStyle={[
              styles.browseContent,
              { paddingHorizontal: pagePadding },
              bottomNavInset ? { paddingBottom: bottomNavInset } : undefined,
            ]}
            columnWrapperStyle={styles.genreRow}
            ListHeaderComponent={
              <Text style={styles.browseTitle}>Browse all</Text>
            }
            renderItem={({ item }) => (
              <FocusablePressable
                accessibilityLabel={`Open ${item.Name ?? "genre"}`}
                onPress={() =>
                  item.Id &&
                  navigation.replace("Music", {
                    view: "browse",
                    genreId: item.Id,
                    genreName: item.Name ?? "Genre",
                  })
                }
                style={(focused, pressed, hovered) => [
                  styles.genreCard,
                  {
                    width: cardSize,
                    height: cardSize,
                    backgroundColor: genreColor(item.Name ?? ""),
                  },
                  (focused || hovered) && styles.genreCardActive,
                  pressed && styles.pressed,
                ]}
              >
                {item.ImageTags?.Primary ? (
                  <Image
                    source={imageSource(credentials, item, "Primary", cardSize)}
                    resizeMode="cover"
                    style={[
                      styles.genreArt,
                      {
                        width: Math.round(cardSize * 0.6),
                        height: Math.round(cardSize * 0.6),
                      },
                    ]}
                  />
                ) : null}
                <Text numberOfLines={3} style={styles.genreName}>
                  {item.Name ?? "Genre"}
                </Text>
              </FocusablePressable>
            )}
            ListEmptyComponent={
              genreLoading ? (
                <ActivityIndicator color={colors.brand} style={styles.loader} />
              ) : (
                <Text style={styles.empty}>
                  No genres found in your music library.
                </Text>
              )
            }
          />
        )}
        {musicScrollbar.indicator}
        </View>
      </View>
      <MatureTopNav navigation={navigation} active="Music" overlay />
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  shell: {
    flex: 1,
    paddingTop: 80,
    paddingHorizontal: 8,
    paddingBottom: 8,
    flexDirection: "row",
    gap: 8,
  },
  shellCompact: { paddingTop: 124, paddingHorizontal: 4, paddingBottom: 0 },
  mainPanel: {
    flex: 1,
    overflow: "hidden",
    borderRadius: 8,
    backgroundColor: "#121212",
  },
  mainList: { flex: 1 },
  content: { paddingBottom: 80 },
  absolute: { position: "absolute", top: 0, left: 0 },
  likedHeader: {
    minHeight: 280,
    padding: 30,
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 24,
    overflow: "hidden",
  },
  likedHeaderCompact: {
    minHeight: 330,
    paddingTop: 80,
    paddingHorizontal: 16,
    paddingBottom: 16,
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-end",
    gap: 20,
  },
  likedArt: {
    width: 200,
    height: 200,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    backgroundColor: "#7154df",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 30,
    elevation: 18,
  },
  likedArtCompact: { width: 120, height: 120 },
  likedHeart: { zIndex: 1 },
  likedCopy: { flex: 1, minWidth: 0, paddingBottom: 8 },
  type: {
    marginBottom: 8,
    color: "#fff",
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "700",
  },
  likedTitle: {
    marginBottom: 12,
    color: "#fff",
    fontSize: 64,
    lineHeight: 70,
    fontWeight: "800",
  },
  likedTitleCompact: { fontSize: 40, lineHeight: 46 },
  summary: { color: "rgba(255,255,255,0.75)", fontSize: 14 },
  controls: { height: 96, paddingHorizontal: 30, justifyContent: "center" },
  controlsCompact: { paddingHorizontal: 12 },
  play: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  playActive: { transform: [{ scale: 1.06 }] },
  trackHeader: {
    height: 34,
    marginHorizontal: 30,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.1)",
    flexDirection: "row",
    alignItems: "center",
  },
  track: {
    minHeight: 56,
    marginHorizontal: 30,
    paddingRight: 8,
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
  },
  trackCompact: { marginHorizontal: 12 },
  trackActive: { backgroundColor: "rgba(255,255,255,0.1)" },
  index: { width: 44, alignItems: "center", justifyContent: "center" },
  indexHeader: { width: 44, color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 14, textAlign: "center" },
  indexText: { color: "#b3b3b3", fontFamily: systemSansFont, fontSize: 14 },
  trackArt: {
    width: 40,
    height: 40,
    marginRight: 12,
    borderRadius: 3,
    backgroundColor: "#282828",
  },
  trackCopy: { flex: 1, minWidth: 0 },
  trackHeading: { flex: 1, color: "#b3b3b3", fontSize: 12 },
  trackTitle: { color: "#fff", fontFamily: systemSansFont, fontSize: 16, lineHeight: 21 },
  trackTitleCurrent: { color: colors.brand },
  trackArtist: { color: "#b3b3b3", fontSize: 13, lineHeight: 17 },
  albumHeading: { width: 300, color: "#b3b3b3", fontSize: 12 },
  album: { width: 300, color: "#b3b3b3", fontSize: 14 },
  timeHeading: {
    width: 72,
    color: "#b3b3b3",
    fontSize: 12,
    textAlign: "right",
  },
  time: { width: 72, color: "#b3b3b3", fontSize: 14, textAlign: "right" },
  browseContent: { paddingTop: 24, paddingBottom: 80 },
  browseTitle: {
    marginBottom: 24,
    color: "#fff",
    fontSize: 24,
    lineHeight: 30,
    fontWeight: "800",
  },
  genreRow: { gap: 16, marginBottom: 16 },
  genreCard: {
    position: "relative",
    flexGrow: 0,
    flexShrink: 0,
    padding: 12,
    borderRadius: 8,
    overflow: "hidden",
    justifyContent: "flex-end",
  },
  genreCardActive: { transform: [{ scale: 1.02 }], opacity: 0.94 },
  genreArt: {
    position: "absolute",
    right: -8,
    bottom: -8,
    borderRadius: 4,
    transform: [{ rotate: "25deg" }],
  },
  genreName: {
    maxWidth: 125,
    color: "#fff",
    fontSize: 16,
    lineHeight: 19,
    fontWeight: "800",
  },
  discographyBack: {
    alignSelf: "flex-start",
    marginBottom: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  discographyBackActive: { opacity: 0.82 },
  discographyBackText: {
    color: "#b3b3b3",
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "600",
  },
  discographyTitle: {
    marginBottom: 20,
    color: "#fff",
    fontSize: 28,
    lineHeight: 34,
    fontWeight: "800",
  },
  discographyTabs: { marginBottom: 24, flexDirection: "row", gap: 8 },
  discographyTab: {
    minHeight: 30,
    paddingHorizontal: 16,
    borderRadius: 20,
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  discographyTabActive: { backgroundColor: "#fff" },
  discographyTabHovered: { transform: [{ scale: 1.03 }] },
  discographyTabText: { color: "#fff", fontSize: 13, fontWeight: "600" },
  discographyTabTextActive: { color: "#000" },
  discographyCard: { flexGrow: 0, flexShrink: 0, borderRadius: 5 },
  discographyArt: {
    overflow: "hidden",
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#282828",
  },
  discographyCardTitle: {
    marginTop: 20,
    color: "#fff",
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "700",
  },
  discographyCardSubtitle: {
    marginTop: 3,
    marginBottom: 8,
    color: "#b3b3b3",
    fontSize: 13,
    lineHeight: 17,
  },
  loader: { marginTop: 60 },
  empty: {
    marginHorizontal: 16,
    marginTop: 24,
    color: "#b3b3b3",
    fontSize: 14,
  },
  pressed: { opacity: 0.76 },
});
